/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/st_simplifypreservetopology.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Simplifies the input geometry by applying a topology-preserving variant of the Douglas-Peucker algorithm with a specified tolerance. Vertices that fall within the tolerance distance from the simplified shape are removed. Unlike `ST_SIMPLIFY`, the resulting geometry is guaranteed to be topologically valid.
 *
 * @example
 * ROW wkt = "POLYGON ((7.998 53.827, 9.470 53.068, 15.754 53.801, 16.523 57.160, 11.162 57.868, 8.064 57.445, 6.219 55.317, 7.998 53.827))"
 * | EVAL simplified = ST_SIMPLIFYPRESERVETOPOLOGY(TO_GEOSHAPE(wkt), 0.7)
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'st_simplifypreservetopology',
  signatures: [
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'tolerance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
